import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import usePageTitle from "../../hooks/usePageTitle";
import {
  RefreshCw,
  Camera,
  AlertCircle,
  CheckCircle,
  Copy,
  ChevronRight,
  ChevronDown,
  MousePointer,
  Bug,
  Loader2,
  Search,
  Minimize2,
  Maximize2,
  X,
  Zap,
  Clock,
  Play,
  Save,
  Ban,
  Wrench
} from "lucide-react";

/*
 * Aurora pass (2026-08-23): PageHeader + a filter toolbar instead of the one-row header, semantic
 * tokens everywhere, and the four columns (list / steps / workspace / AI+fix) stack under lg with
 * the inner panel groups turning vertical. Step/session logic, API calls and copy are unchanged.
 */


/* Shared Aurora class strings. Tailwind needs every class spelled out, so these are full strings. */
const LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
const FIELD = 'bg-secondary/60 border-border';
const PANEL_BAR = 'bg-secondary/60 border-b border-border';

/* How many failed repairs to ask for. The server clamps at 200; whatever we ask for, the reply
 * carries `truncated` and the list says so rather than quietly showing a prefix. */
const FAILURE_LIMIT = 100;

/* true from the lg breakpoint (1024px) up - the side-by-side layout; below it everything stacks */
const DESKTOP_QUERY = '(min-width: 1024px)';
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true
  ));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isDesktop;
};

// ============ XML Tree Parser ============
const parseXmlToTree = (xmlString) => {
  if (!xmlString) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');

    // DOMParser does NOT throw on malformed input - it returns a document whose root is
    // <parsererror>. Without this check the try/catch below never fires for the case it was
    // written for, and a truncated dump renders as a one-node tree labelled "parsererror".
    if (!doc.documentElement || doc.querySelector('parsererror')) {
      console.warn('[ui-debug] dump could not be parsed as XML');
      return { parseError: true };
    }
    
    const buildTree = (node, id = '0') => {
      if (node.nodeType !== 1) return null;
      
      const attrs = {};
      for (const attr of node.attributes || []) {
        attrs[attr.name] = attr.value;
      }
      
      const children = [];
      let childIdx = 0;
      for (const child of node.childNodes) {
        if (child.nodeType === 1) {
          const childTree = buildTree(child, `${id}-${childIdx}`);
          if (childTree) children.push(childTree);
          childIdx++;
        }
      }
      
      return {
        id,
        tag: node.tagName,
        ...attrs,
        children: children.length > 0 ? children : undefined
      };
    };
    
    return buildTree(doc.documentElement);
  } catch (e) {
    console.error('XML parse error:', e);
    return null;
  }
};

// ============ Collapsible Panel Header ============
/**
 * Two shapes, and the difference is not cosmetic.
 *
 * With an `onToggle` this is a real <button>, so it is reachable by Tab and fires on Enter/Space.
 * The previous div[role=button] had neither tabIndex nor onKeyDown: a screen reader announced a
 * collapse control that keyboard users could not reach at all.
 *
 * Without an `onToggle` it is a plain heading bar - no role, no chevron, no aria-expanded. The
 * frames/failures list header is that case: it used to pass `onToggle={() => {}}` and so
 * announced "Collapse Failed repairs" for an action that did nothing.
 */
const PanelHeader = ({ title, icon: Icon, collapsed, onToggle, extra }) => {
  const inner = (
    <>
      <div className="flex min-w-0 items-center gap-2 font-heading text-sm font-semibold text-foreground whitespace-nowrap">
        {Icon && <Icon size={14} className="text-info shrink-0" />}
        <span className="truncate">{title}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
        {extra}
        {onToggle && (collapsed ? <Maximize2 size={14} /> : <Minimize2 size={14} />)}
      </div>
    </>
  );

  const bar = `flex w-full min-h-9 items-center justify-between gap-2 py-1 px-2 ${PANEL_BAR} select-none shrink-0`;

  if (!onToggle) {
    return <div className={bar}>{inner}</div>;
  }
  return (
    <button
      type="button"
      className={`${bar} cursor-pointer text-left`}
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${title}`}
    >
      {inner}
    </button>
  );
};

// ============ Screenshot Viewer with Click-to-Select ============
/**
 * Read the device resolution off the dump itself.
 *
 * The corpus carries no device metadata, and the two dump shapes disagree about where the
 * hierarchy root even is - so this reads width/height from the root when present and falls back
 * to the widest bounds seen anywhere in the tree. It matters: real frames from ONE slot mix
 * 720x1465 and 1080x2400, so a hardcoded size puts every overlay ~50% out on some of them.
 *
 * No <hierarchy> element in the corpus carries a `bounds` attribute - do not "simplify" this to
 * read one.
 */
const screenSizeFromTree = (tree) => {
  if (!tree) return null;
  const w = parseInt(tree.width, 10);
  const h = parseInt(tree.height, 10);
  if (w > 0 && h > 0) return { width: w, height: h };

  let maxX = 0;
  let maxY = 0;
  const walk = (node) => {
    const m = (node.bounds || '').match(/\[(-?\d+),(-?\d+)\]\[(-?\d+),(-?\d+)\]/);
    if (m) {
      maxX = Math.max(maxX, parseInt(m[3], 10));
      maxY = Math.max(maxY, parseInt(m[4], 10));
    }
    (node.children || []).forEach(walk);
  };
  walk(tree);
  return maxX > 0 && maxY > 0 ? { width: maxX, height: maxY } : null;
};

const ScreenshotViewer = ({
  screenshotBase64,
  selectedElement,
  searchHighlights = [],
  xmlTree,
  onElementClick,
  screenWidth,
  screenHeight
}) => {
  const [displayedSize, setDisplayedSize] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Prefer what the dump says over anything the caller assumed.
  const measured = useMemo(() => screenSizeFromTree(xmlTree), [xmlTree]);
  const deviceW = screenWidth || measured?.width || 1080;
  const deviceH = screenHeight || measured?.height || 2400;

  // Update displayed size when image loads or resizes
  useEffect(() => {
    const updateSize = () => {
      if (imgRef.current) {
        const rect = imgRef.current.getBoundingClientRect();
        setDisplayedSize({ width: rect.width, height: rect.height });
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [screenshotBase64]);

  const parseBounds = (boundsStr) => {
    if (!boundsStr) return null;
    // -? on every group: uiautomator routinely emits bounds like [-6,1180][1086,1298] for
    // views under the status bar or partly scrolled off. Without it those elements are silently
    // invisible to both click-to-select and the overlay, with no error anywhere.
    const match = boundsStr.match(/\[(-?\d+),(-?\d+)\]\[(-?\d+),(-?\d+)\]/);
    if (match) {
      return {
        x1: parseInt(match[1]),
        y1: parseInt(match[2]),
        x2: parseInt(match[3]),
        y2: parseInt(match[4]),
      };
    }
    return null;
  };

  const flattenTree = useCallback((node, result = []) => {
    if (!node) return result;
    result.push(node);
    if (node.children) {
      for (const child of node.children) {
        flattenTree(child, result);
      }
    }
    return result;
  }, []);

  const findElementAtPoint = useCallback((clickX, clickY) => {
    if (!xmlTree || displayedSize.width === 0) return null;
    
    // Convert click coordinates to screen coordinates
    const scaleX = deviceW / displayedSize.width;
    const scaleY = deviceH / displayedSize.height;
    const screenX = clickX * scaleX;
    const screenY = clickY * scaleY;
    
    const elements = flattenTree(xmlTree);
    let bestMatch = null;
    let smallestArea = Infinity;
    
    for (const elem of elements) {
      const bounds = parseBounds(elem.bounds);
      if (!bounds) continue;
      
      if (screenX >= bounds.x1 && screenX <= bounds.x2 &&
          screenY >= bounds.y1 && screenY <= bounds.y2) {
        const area = (bounds.x2 - bounds.x1) * (bounds.y2 - bounds.y1);
        if (area < smallestArea && area > 0) {
          smallestArea = area;
          bestMatch = elem;
        }
      }
    }
    
    return bestMatch;
  }, [xmlTree, displayedSize, deviceW, deviceH, flattenTree]);

  const handleImageClick = (e) => {
    if (!imgRef.current) return;
    
    const rect = imgRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    const element = findElementAtPoint(clickX, clickY);
    if (element && onElementClick) {
      onElementClick(element);
    }
  };

  const renderBoundsOverlay = (element, color, key) => {
    const bounds = parseBounds(element?.bounds);
    if (!bounds || displayedSize.width === 0) return null;
    
    // Scale from screen coordinates to displayed image coordinates
    const scaleX = displayedSize.width / deviceW;
    const scaleY = displayedSize.height / deviceH;
    
    return (
      <div
        key={key}
        className={`absolute border-2 pointer-events-none ${color}`}
        style={{
          left: `${bounds.x1 * scaleX}px`,
          top: `${bounds.y1 * scaleY}px`,
          width: `${(bounds.x2 - bounds.x1) * scaleX}px`,
          height: `${(bounds.y2 - bounds.y1) * scaleY}px`,
        }}
      />
    );
  };

  const handleImageLoad = (e) => {
    // Get actual displayed dimensions after CSS scaling
    const rect = e.target.getBoundingClientRect();
    setDisplayedSize({ width: rect.width, height: rect.height });
  };

  return (
    <div ref={containerRef} className="relative h-full overflow-hidden flex items-start justify-center bg-background/60 p-2">
      {screenshotBase64 ? (
        <div className="relative cursor-crosshair h-full max-w-full flex items-start justify-center">
          <img
            ref={imgRef}
            src={/^(blob:|https?:|data:)/.test(screenshotBase64)
              ? screenshotBase64
              : `data:image/png;base64,${screenshotBase64}`}
            alt="Screenshot"
            className="max-w-full object-contain border border-border rounded-lg"
            style={{ maxHeight: 'calc(100% - 8px)', width: 'auto' }}
            onClick={handleImageClick}
            onLoad={handleImageLoad}
          />
          {/* Search highlights (warning / amber) */}
          {searchHighlights.map((elem, idx) => renderBoundsOverlay(elem, 'border-warning bg-warning/20', `search-${idx}`))}
          {/* Selected element (danger / red) */}
          {selectedElement && renderBoundsOverlay(selectedElement, 'border-danger bg-danger/20', 'selected')}
        </div>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-muted-foreground text-sm">No screenshot available</div>
      )}
    </div>
  );
};

// ============ XML Tree Viewer with Highlighting ============
const XmlTreeViewer = ({ 
  xml, 
  selectedElement, 
  searchHighlights = [],
  onElementSelect,
  onElementHover 
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set(['0']));
  const [tree, setTree] = useState(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (xml) {
      const parsed = parseXmlToTree(xml);
      setTree(parsed);
      // Auto-expand root and first few levels
      if (parsed) {
        const toExpand = new Set(['0']);
        const expandLevel = (node, depth) => {
          if (depth > 2) return;
          toExpand.add(node.id);
          if (node.children) {
            node.children.forEach(c => expandLevel(c, depth + 1));
          }
        };
        expandLevel(parsed, 0);
        setExpandedNodes(toExpand);
      }
    }
  }, [xml]);

  // Scroll to selected element
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedElement]);

  // Expand path to selected element
  useEffect(() => {
    if (selectedElement && tree) {
      const findPath = (node, targetId, path = []) => {
        if (node.id === targetId) return [...path, node.id];
        if (node.children) {
          for (const child of node.children) {
            const result = findPath(child, targetId, [...path, node.id]);
            if (result) return result;
          }
        }
        return null;
      };
      
      const path = findPath(tree, selectedElement.id);
      if (path) {
        setExpandedNodes(prev => {
          const next = new Set(prev);
          path.forEach(id => next.add(id));
          return next;
        });
      }
    }
  }, [selectedElement, tree]);

  const toggleExpand = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const isHighlighted = (node) => searchHighlights.some(h => h.id === node.id);
  const isSelected = (node) => selectedElement?.id === node.id;

  const renderNode = (node, depth = 0) => {
    if (!node) return null;
    
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const highlighted = isHighlighted(node);
    const selected = isSelected(node);
    
    const displayText = node.text || node['content-desc'] || node['resource-id']?.split('/').pop() || node.class?.split('.').pop() || node.tag;
    
    return (
      <div key={node.id}>
        <div
          ref={selected ? selectedRef : null}
          className={`
            flex items-center gap-1 py-0.5 px-1 cursor-pointer text-xs font-mono
            hover:bg-secondary/60 rounded
            ${selected ? 'bg-info/15 border-l-2 border-info' : ''}
            ${highlighted && !selected ? 'bg-warning/15 border-l-2 border-warning' : ''}
          `}
          style={{ paddingLeft: `${depth * 12 + 4}px` }}
          onClick={() => onElementSelect?.(node)}
          onMouseEnter={() => onElementHover?.(node)}
          onMouseLeave={() => onElementHover?.(null)}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }}
              className="p-0.5 hover:bg-secondary rounded text-muted-foreground"
              aria-label={isExpanded ? 'Collapse node' : 'Expand node'}
            >
              {isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <span className="text-accent">{node.tag}</span>
          {displayText && displayText !== node.tag && (
            <span className="text-muted-foreground truncate max-w-[200px]">
              : {displayText}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-1">
        {tree ? renderNode(tree) : (
          <div className="text-muted-foreground text-sm p-4">No XML hierarchy loaded</div>
        )}
      </div>
    </ScrollArea>
  );
};

/**
 * An XPath 1.0 string literal for an arbitrary attribute value.
 *
 * XPath 1.0 has no escape inside a literal, so a value containing the quote character in use
 * terminates it early and the whole expression becomes a syntax error. Instagram ships plenty of
 * these - any content-desc with an apostrophe in it ("Sarah's story") - and the 2026-09-04 audit
 * found one in the stored corpus for which Copy -> Test answered ERROR while the identical
 * double-quoted expression answered UNIQUE. Values carrying BOTH quote characters cannot be
 * written as any single literal at all, so they are assembled with concat().
 */
const xpathLiteral = (value) => {
  const s = String(value);
  if (s.indexOf("'") < 0) return `'${s}'`;
  if (s.indexOf('"') < 0) return `"${s}"`;
  const pieces = [];
  s.split("'").forEach((part, i) => {
    if (i > 0) pieces.push('"\'"');
    if (part) pieces.push(`'${part}'`);
  });
  // concat() needs two arguments; a value that is nothing but apostrophes yields one piece.
  return pieces.length === 1 ? pieces[0] : `concat(${pieces.join(', ')})`;
};

// ============ Element Properties Panel ============
const ElementProperties = ({ element, onCopyXpath }) => {
  if (!element) {
    return <div className="text-muted-foreground text-xs p-2">Click on screenshot or tree to select an element</div>;
  }

  const generateXpaths = () => {
    const xpaths = [];
    if (element['resource-id']) {
      xpaths.push(`//*[@resource-id=${xpathLiteral(element['resource-id'])}]`);
    }
    if (element.text) {
      xpaths.push(`//*[@text=${xpathLiteral(element.text)}]`);
    }
    if (element['content-desc']) {
      xpaths.push(`//*[@content-desc=${xpathLiteral(element['content-desc'])}]`);
    }
    if (element.class) {
      xpaths.push(`//*[@class=${xpathLiteral(element.class)}]`);
    }
    return xpaths;
  };

  const properties = [
    { key: 'class', label: 'Class' },
    { key: 'resource-id', label: 'Resource ID' },
    { key: 'text', label: 'Text' },
    { key: 'content-desc', label: 'Content Desc' },
    { key: 'bounds', label: 'Bounds' },
    { key: 'clickable', label: 'Clickable' },
    { key: 'enabled', label: 'Enabled' },
    { key: 'focusable', label: 'Focusable' },
    { key: 'scrollable', label: 'Scrollable' },
    { key: 'package', label: 'Package' },
  ];

  return (
    <div className="space-y-2 p-2 text-xs">
      <div className={LABEL}>Element Properties</div>
      <div className="space-y-1">
        {properties.map(({ key, label }) => (
          element[key] ? (
            <div key={key} className="flex gap-2 min-w-0">
              <span className="text-muted-foreground w-24 shrink-0">{label}:</span>
              <span className="text-foreground break-all min-w-0">{element[key]}</span>
            </div>
          ) : null
        ))}
      </div>

      <div className="mt-3">
        <div className={`${LABEL} mb-1`}>Generated XPaths</div>
        <div className="space-y-1">
          {generateXpaths().map((xpath, idx) => (
            <div key={idx} className="flex items-center gap-1 bg-secondary/60 p-1 rounded-lg">
              <code className="text-success text-xs flex-1 break-all min-w-0">{xpath}</code>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0 tap text-muted-foreground hover:text-foreground"
                onClick={() => onCopyXpath?.(xpath)}
                aria-label="Copy XPath"
                title="Copy XPath"
              >
                <Copy size={10} />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ XPath Search ============
const XPathSearch = ({ xmlTree, onSearchResults }) => {
  const [searchXpath, setSearchXpath] = useState('');
  const [searchCount, setSearchCount] = useState(0);

  const searchByXpath = useCallback(() => {
    if (!xmlTree || !searchXpath.trim()) {
      onSearchResults?.([]);
      setSearchCount(0);
      return;
    }

    const flattenTree = (node, result = []) => {
      if (!node) return result;
      result.push(node);
      if (node.children) {
        node.children.forEach(c => flattenTree(c, result));
      }
      return result;
    };

    const elements = flattenTree(xmlTree);
    const matches = [];

    // Simple XPath attribute matching.
    //
    // The value is matched as EITHER a single- or a double-quoted literal, and the body of each
    // alternative excludes only its own delimiter. The old `['"]([^'"]+)['"]` stopped at the
    // first quote of either kind, so pasting the (correct) //*[@content-desc="Sarah's story"]
    // that the Properties panel now generates searched for `Sarah` and highlighted the wrong
    // nodes with no hint that it had truncated the value.
    const attrMatch = searchXpath.match(/@([\w-]+)\s*=\s*(?:'([^']+)'|"([^"]+)")/);
    const containsMatch = searchXpath.match(
      /contains\s*\(\s*@([\w-]+)\s*,\s*(?:'([^']+)'|"([^"]+)")\s*\)/);

    for (const elem of elements) {
      let matched = false;

      if (containsMatch) {
        const [, attr, single, double] = containsMatch;
        const value = single !== undefined ? single : double;
        if (elem[attr] && elem[attr].toLowerCase().includes(value.toLowerCase())) {
          matched = true;
        }
      } else if (attrMatch) {
        const [, attr, single, double] = attrMatch;
        const value = single !== undefined ? single : double;
        if (elem[attr] === value) {
          matched = true;
        }
      } else if (searchXpath.includes('*') || searchXpath.includes('//')) {
        // Match by class name in xpath
        const classMatch = searchXpath.match(/\/\/(\w+(?:\.\w+)*)/);
        if (classMatch && elem.class?.includes(classMatch[1])) {
          matched = true;
        }
      }

      // Also match by simple text search in any attribute
      if (!matched && searchXpath.length > 2) {
        const searchLower = searchXpath.toLowerCase();
        if (
          elem.text?.toLowerCase().includes(searchLower) ||
          elem['resource-id']?.toLowerCase().includes(searchLower) ||
          elem['content-desc']?.toLowerCase().includes(searchLower) ||
          elem.class?.toLowerCase().includes(searchLower)
        ) {
          matched = true;
        }
      }

      if (matched) matches.push(elem);
    }

    setSearchCount(matches.length);
    onSearchResults?.(matches);
  }, [xmlTree, searchXpath, onSearchResults]);

  return (
    <div className={`flex items-center gap-2 p-2 ${PANEL_BAR}`}>
      <Search size={14} className="text-muted-foreground shrink-0" />
      <Input
        value={searchXpath}
        onChange={(e) => setSearchXpath(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && searchByXpath()}
        placeholder="Search XPath or text..."
        aria-label="Search XPath or text"
        className={`flex-1 min-w-0 h-8 text-xs ${FIELD} placeholder:text-muted-foreground/70`}
      />
      <Button size="sm" variant="secondary" className="h-8 text-xs shrink-0" onClick={searchByXpath}>
        Search
      </Button>
      {searchCount > 0 && (
        <Badge className="bg-warning/15 text-warning border-transparent tnum shrink-0">{searchCount} found</Badge>
      )}
      {searchXpath && (
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 tap shrink-0 text-muted-foreground hover:text-foreground"
          onClick={() => { setSearchXpath(''); onSearchResults?.([]); setSearchCount(0); }}
          aria-label="Clear search"
        >
          <X size={12} />
        </Button>
      )}
    </div>
  );
};

// ============ Logs Viewer ============

/*
 * V3 container, 2026-09-03.
 *
 * WHAT CHANGED AND WHY
 * The V2 page spoke to a Python/FastAPI backend over 14 raw fetches to /api/ui-debug/*. None of
 * those routes exists on this Spring server, so every one of them 404'd and the page rendered an
 * empty shell with console errors nobody saw. This container talks to /uiapi/admin/ui-debug
 * through the shared axios client, which brings session auth and the XSRF header with it.
 *
 * THREE SUBSYSTEMS WERE CUT, not ported:
 *   - Step Sessions   (ui_debug_step_sessions: a Supabase table with no Spring counterpart)
 *   - Live Capture    (/api/detected-devices/*: V2's PC-agent WebSocket, which does not exist here)
 *   - RDS file fetch  (interpolated a caller-supplied path into `powershell -Command
 *                      "Get-Content -Path '{path}'"` with no escaping and no auth - never port it)
 *
 * WHAT REPLACED THEM: two corpora that already exist on this stack.
 *   - Failed heals  - the exact screen the automatic repair gave up on. Precise, and the reason
 *                     this page exists: a heal that fails is escalated to a person who fixes it
 *                     by looking at the screen.
 *   - Error frames  - the png+xml pairs the worker has always uploaded on its error path. Broad,
 *                     and the only corpus with pictures.
 *
 * THE ADDITION V2 NEVER HAD: every XPath can be tested server-side against the very dump on
 * screen, using LocatorValidator - the same engine the runner evaluates with. AMBIGUOUS is a real
 * answer, not a warning: the runner takes item(0) with no ambiguity check, so a two-match locator
 * acts on the wrong element silently.
 */

/** Frame names carry a per-session counter that RESETS, so they are not a sequence. Use mtime. */
const whenFrame = (millis) => {
  if (!millis) return 'unknown time';
  try {
    return new Date(Number(millis)).toLocaleString();
  } catch (e) {
    return 'unknown time';
  }
};

/**
 * The server's own words out of an error response, whatever shape axios handed back.
 *
 * The four JSON endpoints give `{error: "..."}` as a parsed object, but the xml/screenshot ones
 * are requested with responseType text/blob, so the same JSON arrives as an unparsed string or a
 * Blob. A Blob can only be read asynchronously, so it yields '' here and the caller falls back to
 * its no-reason wording rather than pretending to know.
 */
const serverSaid = (data) => {
  if (!data) return '';
  if (typeof data === 'string') {
    const body = data.trim();
    if (!body) return '';
    if (body.charAt(0) === '{') {
      try {
        const parsed = JSON.parse(body);
        if (parsed && typeof parsed.error === 'string') return parsed.error;
      } catch (notJsonAfterAll) {
        console.warn('[ui-debug] error body started like JSON but did not parse:',
          notJsonAfterAll.message);
      }
    }
    return body.slice(0, 200);
  }
  if (typeof data === 'object' && typeof data.error === 'string') return data.error;
  return '';
};

const VERDICT_TONE = {
  UNIQUE: 'bg-success/15 text-success border-success/30',
  AMBIGUOUS: 'bg-warning/15 text-warning border-warning/30',
  NONE: 'bg-muted text-muted-foreground border-border',
  ERROR: 'bg-destructive/15 text-destructive border-destructive/30',
};

/**
 * An override row's timestamps arrive as LocalDateTime.toString() ("2026-09-05T10:11:12.123456"),
 * not epoch millis like the frames, so whenFrame() cannot read them. To the minute is enough for a
 * table cell; the full string goes on the cell's title.
 */
const whenOverride = (iso) => (
  typeof iso === 'string' && iso.length >= 16 ? iso.slice(0, 16).replace('T', ' ') : (iso || '')
);

/**
 * A blank page is the one failure a diagnostic tool must never produce.
 *
 * Without this, a single unexpected payload shape unmounts the whole route and the operator sees
 * nothing at all - with no clue whether the backend is down or there is simply no data.
 */
class DebugErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ui-debug] render failed', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="m-4 rounded-lg border border-destructive/40 bg-destructive/10 p-4">
        <div className="flex items-center gap-2 font-heading text-sm font-semibold text-destructive">
          <AlertCircle size={16} /> This page hit an error while rendering
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          The data loaded but could not be displayed. The details are in the browser console.
        </p>
        <pre className="mt-2 max-h-40 overflow-auto rounded bg-secondary/60 p-2 text-xs">
          {String(this.state.error?.message || this.state.error)}
        </pre>
        <Button className="mt-3" size="sm" variant="outline" onClick={() => window.location.reload()}>
          <RefreshCw size={14} className="mr-1" /> Reload
        </Button>
      </div>
    );
  }
}

function UIDebugViewer() {
  usePageTitle('UI Debug');

  // 'failures' = screens the AI gave up on. 'frames' = the worker's error screenshots.
  const [mode, setMode] = useState('failures');

  const [failures, setFailures] = useState([]);
  const [corpus, setCorpus] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [frames, setFrames] = useState([]);
  // The informational note the server attaches to a listing (the mtime-ordering caveat, mostly).
  // Rendered under the list in BOTH modes now - it used to be frames-only.
  const [listNote, setListNote] = useState('');
  // The narrower case: the server told us the corpus does not exist on this machine at all
  // ("failed-heal recording is not available", "no corpus directory on this server yet"). That is
  // a broken deployment, not an empty queue, and it must never render as "that is good news".
  const [unavailableNote, setUnavailableNote] = useState('');
  // The server's `field` filter on the failures queue. Applied on Enter rather than per keystroke,
  // because loadFailures is a useEffect dependency and would refetch on every character.
  const [fieldInput, setFieldInput] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');

  const [selected, setSelected] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [xmlContent, setXmlContent] = useState('');
  const [xmlTree, setXmlTree] = useState(null);
  const [parseFailed, setParseFailed] = useState(false);

  const [selectedElement, setSelectedElement] = useState(null);
  const [searchHighlights, setSearchHighlights] = useState([]);

  const [loading, setLoading] = useState(false);
  // "The backend is gone" and "there is no data" must never look the same on a diagnostic page.
  const [loadError, setLoadError] = useState(null);
  const [flagOff, setFlagOff] = useState(false);

  const [xpathToTest, setXpathToTest] = useState('');
  const [xpathResult, setXpathResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // ---- locator overrides: the permanent fix (V2's xpath_overrides, now locator_override) ----
  // The version filter is an input/committed pair like fieldInput/fieldFilter, for the same reason:
  // loadOverrides is an effect dependency. '' means every version - the server then answers the
  // newest 200 across versions.
  const [overrideVersionInput, setOverrideVersionInput] = useState('');
  const [overrideVersion, setOverrideVersion] = useState('');
  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overridesError, setOverridesError] = useState(null);
  const [retiringId, setRetiringId] = useState(null);
  // The save-from-tester block. A failure carries both its failed locator (the original) and its
  // Instagram version; a plain frame carries neither, so these two inputs are only consulted when
  // the tested context has no value of its own.
  const [overrideOriginalInput, setOverrideOriginalInput] = useState('');
  const [overrideIgVersionInput, setOverrideIgVersionInput] = useState('');
  const [overrideNotes, setOverrideNotes] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  // {ok, text}: the server's `proof` line on success, its `error` verbatim on refusal.
  const [overrideSaveResult, setOverrideSaveResult] = useState(null);

  const [collapsedPanels, setCollapsedPanels] = useState({
    screenshot: false, tree: false, props: false, test: false, overrides: false,
  });
  const togglePanel = (k) => setCollapsedPanels((p) => ({ ...p, [k]: !p[k] }));

  const isDesktop = useIsDesktop();

  // Monotonic request token: a slow response for frame A must never land over frame B. Every
  // await in openScreen re-checks it, and a stale response is dropped on the floor.
  const openSeq = useRef(0);

  const describeError = useCallback((err, what) => {
    const status = err?.response?.status;
    const said = serverSaid(err?.response?.data);
    // Deep logging: this page runs on machines nobody here has seen, and the difference between
    // "flag off", "not an admin any more" and "stale CSRF token" is invisible in the UI alone.
    console.error('[ui-debug]', what, '->',
      String(err?.config?.method || 'get').toUpperCase(), err?.config?.url || '(no url)',
      'status', status === undefined ? '(no response)' : status,
      'body', err?.response?.data, err);

    if (status === 403) {
      // Only a denial that NAMES the flag is the flag. StaffGate answers "admin only" for a lost
      // role, and a rejected CSRF token is refused by Spring Security before any controller runs
      // - there is no AccessDeniedHandler on /uiapi, so that one arrives with an empty body.
      // Rendering all three as "turn the flag on in Admin -> Feature Flags" sent operators to a
      // switch that was already on.
      if (/feature flag/i.test(said)) {
        setFlagOff(true);
        return `${what}: refused (403). ${said}`;
      }
      setFlagOff(false);
      if (said) {
        return `${what}: refused (403) - ${said}. This is not the feature flag; the session no`
          + ' longer has ROLE_ADMIN.';
      }
      return `${what}: refused (403) with no reason from the server. That is the shape of a`
        + ' rejected CSRF token - reload the page to get a fresh one. If it persists, the'
        + ' session has lost ROLE_ADMIN.';
    }
    if (status === 401) return `${what}: not logged in (401). Sign in again.`;
    if (status) return `${what}: server returned ${status}${said ? ` - ${said}` : ''}.`;
    return `${what}: could not reach the server (${err?.message || 'network error'}).`;
  }, []);

  /**
   * Report a 200 that carries a failure.
   *
   * /slots and /frames answer 200 with an `error` field when the corpus directory cannot be read,
   * and /failures answers 200 with a `note` when the FailedHeal repository is not wired at all.
   * Both used to render as an empty list - exactly the "the backend is gone looks like there is
   * no data" the state above forbids.
   */
  const reportPayloadProblem = useCallback((data, what) => {
    const problem = typeof data?.error === 'string' ? data.error : '';
    if (problem) {
      const msg = `${what}: ${problem}`;
      console.error('[ui-debug] 200 with an error field -', what, data);
      setLoadError(msg);
      toast.error(msg);
      return true;
    }
    return false;
  }, []);

  // ---------------------------------------------------------------- loaders

  const loadFailures = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setListNote('');
    setUnavailableNote('');
    try {
      const params = { limit: FAILURE_LIMIT };
      if (fieldFilter) params.field = fieldFilter;
      const { data } = await api.get('/admin/ui-debug/failures', { params });
      setFailures(data?.rows || []);
      setFlagOff(false);
      // The server sends this note INSTEAD of rows when FailedHealRepository is not wired, so an
      // empty list here does not mean "no repair ever failed".
      if (data?.note) {
        setListNote(data.note);
        setUnavailableNote(data.note);
        console.warn('[ui-debug] /failures answered with a note instead of rows:', data.note);
      }
      reportPayloadProblem(data, 'Could not load failed repairs');
      // The list is capped server-side (and by FAILURE_LIMIT here). Saying nothing about the cap
      // reads as "this is the whole queue", which is exactly the wrong belief on a triage page.
      if (data?.truncated) {
        toast.warning(`Showing the newest ${data.count} failed repairs only - there are more.`
          + ' Filter by logical field to narrow the queue.');
      }
    } catch (err) {
      setFailures([]);
      const msg = describeError(err, 'Could not load failed repairs');
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [describeError, reportPayloadProblem, fieldFilter]);

  const loadCorpus = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setListNote('');
    setUnavailableNote('');
    try {
      const { data } = await api.get('/admin/ui-debug/slots');
      setCorpus(data?.users || []);
      setFlagOff(false);
      if (data?.note) {
        setListNote(data.note);
        setUnavailableNote(data.note);
        console.warn('[ui-debug] /slots answered with a note instead of users:', data.note);
      }
      // A 200 carrying `error` means the corpus directory could not be read at all - an empty
      // user list then means "the disk said no", not "no slot has ever failed".
      reportPayloadProblem(data, 'Could not list stored frames');
    } catch (err) {
      setCorpus([]);
      const msg = describeError(err, 'Could not list stored frames');
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [describeError, reportPayloadProblem]);

  const loadFrames = useCallback(async (user, slot) => {
    if (!user || !slot) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get('/admin/ui-debug/frames', { params: { user, slot } });
      setFrames(data?.frames || []);
      setListNote(data?.note || '');
      setFlagOff(false);
      reportPayloadProblem(data, `Could not list frames for ${user}/${slot}`);
      if (data?.truncated) {
        toast.warning(`Showing the newest ${data.count} frames only - there are more on disk.`);
      }
    } catch (err) {
      setFrames([]);
      const msg = describeError(err, 'Could not load that slot');
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [describeError, reportPayloadProblem]);

  useEffect(() => {
    if (mode === 'failures') loadFailures();
    else loadCorpus();
  }, [mode, loadFailures, loadCorpus]);

  useEffect(() => {
    if (selectedUser && selectedSlot) loadFrames(selectedUser, selectedSlot);
  }, [selectedUser, selectedSlot, loadFrames]);

  // ---------------------------------------------------------------- opening one screen

  /**
   * Clear FIRST, then load.
   *
   * The V2 version set the new screenshot and left the previous tree in place when the XML fetch
   * failed - so click-to-select resolved the new picture against the old bounds and confidently
   * reported the wrong element. Nothing about that failure was visible.
   */
  const openScreen = useCallback(async (entry) => {
    const seq = ++openSeq.current;
    setSelected(entry);
    setScreenshotUrl(null);
    setXmlContent('');
    setXmlTree(null);
    setParseFailed(false);
    setSelectedElement(null);
    setSearchHighlights([]);
    setXpathResult(null);
    setLoading(true);
    try {
      let xml = '';
      if (entry.kind === 'failure') {
        const { data } = await api.get(`/admin/ui-debug/failures/${entry.id}/xml`,
          { responseType: 'text' });
        if (seq !== openSeq.current) return;
        xml = typeof data === 'string' ? data : String(data ?? '');
      } else {
        const params = { user: entry.user, slot: entry.slot, name: entry.name };
        const { data } = await api.get('/admin/ui-debug/frame/xml',
          { params, responseType: 'text' });
        xml = typeof data === 'string' ? data : String(data ?? '');
        if (entry.hasScreenshot) {
          // A real URL with a real Content-Type, not a data: URI guessed to be JPEG.
          const q = new URLSearchParams(params).toString();
          const img = await api.get(`/admin/ui-debug/frame/screenshot?${q}`,
            { responseType: 'blob' });
          if (seq !== openSeq.current) return;
          setScreenshotUrl(URL.createObjectURL(img.data));
        }
      }
      setXmlContent(xml);
      const tree = parseXmlToTree(xml);
      if (tree?.parseError) {
        // Log the head of the body: "truncated dump" and "the dev proxy served an HTML page for
        // a route it did not know" both land here and look identical in the UI.
        console.error('[ui-debug] dump did not parse as XML for',
          entry.kind, entry.id ?? entry.name, '-', xml.length, 'chars, starts:',
          xml.slice(0, 200));
        setParseFailed(true);
        setXmlTree(null);
      } else {
        setXmlTree(tree);
      }
    } catch (err) {
      if (seq !== openSeq.current) return;
      const msg = describeError(err, 'Could not open that screen');
      setLoadError(msg);
      toast.error(msg);
    } finally {
      if (seq === openSeq.current) setLoading(false);
    }
  }, [describeError]);

  // Blob URLs are per-object; without this every frame opened leaks one for the session.
  useEffect(() => () => { if (screenshotUrl) URL.revokeObjectURL(screenshotUrl); },
    [screenshotUrl]);

  // ---------------------------------------------------------------- the XPath tester

  const runXpathTest = useCallback(async (candidate) => {
    const xpath = (candidate ?? xpathToTest).trim();
    if (!xpath) { toast.error('Enter an XPath to test'); return; }
    if (!selected) { toast.error('Open a screen first'); return; }
    setTesting(true);
    setXpathResult(null);
    try {
      const body = selected.kind === 'failure'
        ? { xpath, failureId: selected.id }
        : { xpath, user: selected.user, slot: selected.slot, name: selected.name };
      const { data } = await api.post('/admin/ui-debug/xpath/test', body);
      setXpathResult({ ...data, xpath });
      if (data?.verdict === 'UNIQUE') toast.success('Resolves to exactly one node');
      else if (data?.verdict === 'AMBIGUOUS') toast.warning(`Matches ${data.matches} nodes`);
    } catch (err) {
      toast.error(describeError(err, 'Could not test that XPath'));
    } finally {
      setTesting(false);
    }
  }, [xpathToTest, selected, describeError]);

  const copyXpath = useCallback((xpath) => {
    navigator.clipboard.writeText(xpath);
    toast.success('XPath copied');
    setXpathToTest(xpath);
  }, []);

  const handleSearchResults = useCallback((results) => setSearchHighlights(results || []), []);

  // ---------------------------------------------------------------- locator overrides

  const loadOverrides = useCallback(async () => {
    setOverridesLoading(true);
    setOverridesError(null);
    try {
      const params = overrideVersion ? { igVersion: overrideVersion } : {};
      const { data } = await api.get('/admin/ui-debug/overrides', { params });
      setOverrides(data?.overrides || []);
      setFlagOff(false);
    } catch (err) {
      setOverrides([]);
      // Panel only, no toast: the failures/frames loader already toasts the same 403 when the
      // flag is off, and two toasts for one switched-off switch is noise. describeError still
      // logs it and still raises the flag banner.
      setOverridesError(describeError(err, 'Could not load overrides'));
    } finally {
      setOverridesLoading(false);
    }
  }, [overrideVersion, describeError]);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  // Follow the screen on view. A failure carries the Instagram version it was recorded on, and the
  // overrides that matter while looking at it are that version's. Frames carry no version at all
  // (the /frames listing has name, size and mtime only), so opening one leaves the filter alone.
  useEffect(() => {
    if (selected?.igVersion) {
      setOverrideVersionInput(selected.igVersion);
      setOverrideVersion(selected.igVersion);
    }
  }, [selected]);

  // A new test or a new screen makes the last save's outcome line stale.
  useEffect(() => { setOverrideSaveResult(null); }, [selected, xpathResult]);

  const retireOverride = useCallback(async (row) => {
    const reason = window.prompt(
      `Retire override #${row.id}? Every runner stops using it at its next session start.\n\n`
      + 'Reason (one line):', '');
    if (reason === null) return;   // cancelled
    setRetiringId(row.id);
    setOverridesError(null);
    try {
      const { data } = await api.post(`/admin/ui-debug/overrides/${row.id}/retire`,
        { reason: reason.trim() });
      toast.success(data?.note === 'already retired'
        ? `Override #${row.id} was already retired`
        : `Override #${row.id} retired`);
      await loadOverrides();
    } catch (err) {
      const msg = describeError(err, `Could not retire override #${row.id}`);
      setOverridesError(msg);
      toast.error(msg);
    } finally {
      setRetiringId(null);
    }
  }, [loadOverrides, describeError]);

  /**
   * The tested XPath becomes the permanent replacement for the original locator. The server
   * re-validates it against the same stored screen (failureId, or user/slot/name for a frame) and
   * refuses with 422 unless it resolves to exactly one node - the verdict in that `error` is shown
   * as the server wrote it.
   */
  const saveOverride = useCallback(async () => {
    if (!selected || xpathResult?.verdict !== 'UNIQUE' || !xpathResult.xpath) {
      toast.error('Test an XPath first - only a UNIQUE verdict can be saved');
      return;
    }
    const original = (selected.failedLocator || overrideOriginalInput).trim();
    const igVersion = (selected.igVersion || overrideIgVersionInput || overrideVersion).trim();
    if (!original) { toast.error('Enter the original locator this override replaces'); return; }
    if (!igVersion) { toast.error('Enter the Instagram version this override is for'); return; }
    const body = {
      original,
      replacement: xpathResult.xpath,
      igVersion,
      notes: overrideNotes.trim() || undefined,
      ...(selected.kind === 'failure'
        ? { failureId: selected.id }
        : { user: selected.user, slot: selected.slot, name: selected.name }),
    };
    setSavingOverride(true);
    setOverrideSaveResult(null);
    try {
      const { data } = await api.post('/admin/ui-debug/overrides', body);
      setOverrideSaveResult({
        ok: true,
        text: `Saved as override #${data?.id} for Instagram ${data?.igVersion}: `
          + (data?.proof || 'the server sent no proof line'),
      });
      toast.success(`Override #${data?.id} saved`);
      // Show the row: switch the list to its version, or refetch if it is there already.
      if (igVersion !== overrideVersion) {
        setOverrideVersionInput(igVersion);
        setOverrideVersion(igVersion);
      } else {
        loadOverrides();
      }
    } catch (err) {
      const msg = describeError(err, 'Could not save the override');
      setOverrideSaveResult({ ok: false, text: msg });
      toast.error(msg);
    } finally {
      setSavingOverride(false);
    }
  }, [selected, xpathResult, overrideOriginalInput, overrideIgVersionInput, overrideVersion,
    overrideNotes, describeError, loadOverrides]);

  // ---------------------------------------------------------------- the list

  const listRows = useMemo(() => {
    if (mode === 'failures') {
      return (failures || []).filter(Boolean).map((f) => ({
        kind: 'failure',
        id: f.id,
        // Carried through for the overrides panel: the failed locator IS the original an override
        // replaces, and the version is the bucket it lives in.
        igVersion: f.igVersion || '',
        failedLocator: f.failedLocator || '',
        title: f.logicalField || f.failedLocator || `#${f.id}`,
        subtitle: f.problem || '',
        // The server stringifies observedAt with String.valueOf, so a null column arrives as the
        // four-character string "null" - which filter(Boolean) keeps and the row then displays.
        meta: [f.slot, f.igVersion, f.observedAt]
          .filter((v) => v && v !== 'null' && v !== 'undefined').join(' · '),
        tone: 'destructive',
      }));
    }
    return (frames || []).filter((f) => f && f.name).map((f) => ({
      kind: 'frame',
      id: `${selectedUser}|${selectedSlot}|${f.name}`,
      user: selectedUser,
      slot: selectedSlot,
      name: f.name,
      hasScreenshot: f.hasScreenshot,
      title: f.name,
      subtitle: whenFrame(f.modifiedAtMillis),
      meta: `${Math.round((f.xmlBytes || 0) / 1024)} KB hierarchy`
        + (f.hasScreenshot ? ` · ${Math.round((f.screenshotBytes || 0) / 1024)} KB image` : ' · no image'),
      tone: 'muted',
    }));
  }, [mode, failures, frames, selectedUser, selectedSlot]);

  const userOptions = (corpus || []).filter((u) => u && u.user);
  const slotOptions = (userOptions.find((u) => u.user === selectedUser)?.slots || [])
    .filter(Boolean);

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <PageHeader
        icon={Bug}
        title="UI Debug"
        description="Inspect the screens automation failed on, and test locators against them"
        actions={
          <Button size="sm" variant="outline"
            onClick={() => (mode === 'failures' ? loadFailures() : loadCorpus())}>
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      {flagOff && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <span className="font-semibold">This surface is switched off.</span>{' '}
          It needs ROLE_ADMIN and the <code className="font-mono">UIAPI_UI_DEBUG</code> feature
          flag, which you can turn on from Admin → Feature Flags.
        </div>
      )}
      {loadError && !flagOff && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-destructive" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={mode === 'failures' ? 'default' : 'outline'}
          onClick={() => { setMode('failures'); setSelected(null); }}>
          <Zap size={14} className="mr-1" /> Failed repairs
        </Button>
        <Button size="sm" variant={mode === 'frames' ? 'default' : 'outline'}
          onClick={() => { setMode('frames'); setSelected(null); }}>
          <Camera size={14} className="mr-1" /> Error frames
        </Button>

        {mode === 'failures' && (
          <>
            {/* The server has always supported ?field=; without this the only way to find one
                logical field's failures in a capped list was to scroll and hope. */}
            <Input
              value={fieldInput}
              onChange={(e) => setFieldInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setFieldFilter(fieldInput.trim()); }}
              onBlur={() => setFieldFilter(fieldInput.trim())}
              placeholder="Filter by logical field, then Enter"
              aria-label="Filter failed repairs by logical field"
              className={`h-8 w-64 text-xs ${FIELD} placeholder:text-muted-foreground/70`}
            />
            {fieldFilter && (
              <Button size="sm" variant="ghost" className="h-8 px-2 text-xs"
                onClick={() => { setFieldInput(''); setFieldFilter(''); }}>
                <X size={12} className="mr-1" /> Clear filter
              </Button>
            )}
          </>
        )}

        {mode === 'frames' && (
          <>
            <Select value={selectedUser} onValueChange={(v) => { setSelectedUser(v); setSelectedSlot(''); }}>
              <SelectTrigger className={`h-8 w-56 ${FIELD}`}>
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                {userOptions.map((u) => (
                  <SelectItem key={u.user} value={u.user}>{u.user}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedSlot} onValueChange={setSelectedSlot} disabled={!selectedUser}>
              <SelectTrigger className={`h-8 w-40 ${FIELD}`}>
                <SelectValue placeholder="Select slot" />
              </SelectTrigger>
              <SelectContent>
                {slotOptions.map((sl) => (
                  <SelectItem key={sl} value={sl}>{sl}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      <div className={`flex min-h-0 flex-1 ${isDesktop ? 'flex-row gap-3' : 'flex-col gap-3'}`}>
        {/* ---- the list ---- */}
        <div className={`flex min-h-0 flex-col rounded-lg border border-border ${isDesktop ? 'w-80 shrink-0' : 'max-h-64'}`}>
          {/* No onToggle: this header does not collapse, and pretending it does announced a
              control to screen readers that did nothing. */}
          <PanelHeader
            title={mode === 'failures' ? 'Failed repairs' : 'Frames'}
            icon={mode === 'failures' ? Zap : Camera}
            extra={<span className="text-xs">{listRows.length}</span>}
          />
          <ScrollArea className="min-h-0 flex-1">
            {listRows.length === 0 && (
              <p className="p-3 text-sm text-muted-foreground">
                {loading ? 'Loading…'
                  : loadError ? 'Nothing could be loaded - see the message above.'
                  // The server said the corpus is not there at all. That is not "no data".
                  : unavailableNote ? unavailableNote
                  : mode === 'failures'
                    ? 'No failed repairs recorded. That is good news: it means every repair the '
                      + 'automation attempted found a working locator.'
                    : selectedUser && selectedSlot ? 'No stored frames for this slot.'
                      : 'Pick a user and slot.'}
              </p>
            )}
            {listRows.map((row) => (
              <button
                key={row.id}
                onClick={() => openScreen(row)}
                className={`w-full border-b border-border px-3 py-2 text-left hover:bg-secondary/60
                  ${selected && String(selected.id) === String(row.id) ? 'bg-secondary' : ''}`}
              >
                <div className="truncate font-mono text-xs font-semibold text-foreground">
                  {row.title}
                </div>
                {row.subtitle && (
                  <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{row.subtitle}</div>
                )}
                {row.meta && (
                  <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{row.meta}</div>
                )}
              </button>
            ))}
          </ScrollArea>
          {listNote && (
            <p className="border-t border-border px-3 py-2 text-[11px] leading-snug text-muted-foreground">
              <Clock size={11} className="mr-1 inline" />{listNote}
            </p>
          )}
        </div>

        {/* ---- the workspace ---- */}
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {!selected && (
            <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-border">
              <p className="max-w-md p-6 text-center text-sm text-muted-foreground">
                Select an entry to see the screen it failed on. You can click the screenshot to
                pick an element, browse the hierarchy, and test an XPath against this exact dump.
              </p>
            </div>
          )}

          {selected && (
            <div className={`flex min-h-0 flex-1 ${isDesktop ? 'flex-row gap-3' : 'flex-col gap-3'}`}>
              <div className={`flex min-h-0 flex-col rounded-lg border border-border ${isDesktop ? 'w-[38%]' : ''}`}>
                <PanelHeader title="Screenshot" icon={Camera}
                  collapsed={collapsedPanels.screenshot}
                  onToggle={() => togglePanel('screenshot')} />
                {!collapsedPanels.screenshot && (
                  <div className="min-h-0 flex-1 overflow-auto p-2">
                    {screenshotUrl ? (
                      <ScreenshotViewer
                        screenshotBase64={screenshotUrl}
                        selectedElement={selectedElement}
                        searchHighlights={searchHighlights}
                        xmlTree={xmlTree}
                        onElementClick={setSelectedElement}
                      />
                    ) : (
                      <p className="p-4 text-sm text-muted-foreground">
                        No screenshot for this entry. Failed repairs keep the view hierarchy only -
                        the runner does not take a picture at the point a lookup fails.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex min-h-0 flex-1 flex-col gap-3">
                <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-border">
                  <PanelHeader title="Hierarchy" icon={ChevronRight}
                    collapsed={collapsedPanels.tree} onToggle={() => togglePanel('tree')}
                    extra={<span className="text-xs">{Math.round((xmlContent || '').length / 1024)} KB</span>} />
                  {!collapsedPanels.tree && (
                    <div className="flex min-h-0 flex-1 flex-col">
                      <XPathSearch xmlTree={xmlTree} onSearchResults={handleSearchResults} />
                      {parseFailed ? (
                        <div className="p-3 text-sm text-warning">
                          <p>
                            This dump could not be parsed as XML. A truncated upload is the usual
                            cause, but a 200 that is not a dump at all looks identical here - a
                            dev-proxy HTML page for an unknown route, for instance. The first
                            80 characters of what arrived say which:
                          </p>
                          <code className="mt-1 block break-all rounded bg-secondary/60 p-1 font-mono text-[11px] text-muted-foreground">
                            {(xmlContent || '').replace(/\s+/g, ' ').trim().slice(0, 80)
                              || '(the response was empty)'}
                          </code>
                        </div>
                      ) : (
                        <div className="min-h-0 flex-1 overflow-auto">
                          <XmlTreeViewer
                            xml={xmlContent}
                            selectedElement={selectedElement}
                            onElementSelect={setSelectedElement}
                            searchHighlights={searchHighlights}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex min-h-0 flex-col rounded-lg border border-border">
                  <PanelHeader title="Properties" icon={MousePointer}
                    collapsed={collapsedPanels.props} onToggle={() => togglePanel('props')} />
                  {!collapsedPanels.props && (
                    <div className="max-h-56 min-h-0 overflow-auto">
                      <ElementProperties element={selectedElement} onCopyXpath={copyXpath} />
                    </div>
                  )}
                </div>

                {/* The piece V2 never had. */}
                <div className="flex min-h-0 flex-col rounded-lg border border-border">
                  <PanelHeader title="Test XPath against this screen" icon={CheckCircle}
                    collapsed={collapsedPanels.test} onToggle={() => togglePanel('test')} />
                  {!collapsedPanels.test && (
                    <div className="space-y-2 p-2">
                      <div className="flex gap-2">
                        <Input
                          value={xpathToTest}
                          onChange={(e) => setXpathToTest(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') runXpathTest(); }}
                          placeholder="//*[@resource-id='com.instagram.android:id/…']"
                          className={`h-8 font-mono text-xs ${FIELD}`}
                        />
                        <Button size="sm" onClick={() => runXpathTest()} disabled={testing}>
                          {testing ? <Loader2 size={14} className="animate-spin" />
                            : <><Play size={14} className="mr-1" /> Test</>}
                        </Button>
                      </div>
                      {xpathResult && (
                        <div className={`rounded-md border px-2 py-1.5 text-xs ${VERDICT_TONE[xpathResult.verdict] || VERDICT_TONE.ERROR}`}>
                          <div className="font-semibold">
                            {xpathResult.verdict}
                            {typeof xpathResult.matches === 'number'
                              && ` · ${xpathResult.matches} match${xpathResult.matches === 1 ? '' : 'es'}`}
                          </div>
                          <div className="mt-1 leading-snug opacity-90">{xpathResult.advice}</div>
                          {(xpathResult.text || xpathResult.resourceId) && (
                            <div className="mt-1 font-mono opacity-80">
                              {xpathResult.text ? `text: ${xpathResult.text}` : ''}
                              {xpathResult.resourceId ? `  id: ${xpathResult.resourceId}` : ''}
                            </div>
                          )}
                          {xpathResult.error && (
                            <div className="mt-1 opacity-90">{xpathResult.error}</div>
                          )}
                        </div>
                      )}
                      {/* Only a UNIQUE verdict can become the permanent fix. */}
                      {xpathResult?.verdict === 'UNIQUE' && (
                        <div className="space-y-2 rounded-md border border-border bg-secondary/40 p-2">
                          <div className={LABEL}>Save as permanent override</div>
                          <p className="text-[11px] leading-snug text-muted-foreground">
                            Every runner uses the tested XPath instead of the original locator on
                            this Instagram version from its next session start. The server checks
                            it once more against this exact screen before saving.
                          </p>
                          {selected?.failedLocator ? (
                            <div className="flex min-w-0 gap-2 text-xs">
                              <span className="w-24 shrink-0 text-muted-foreground">Original:</span>
                              <code className="min-w-0 break-all font-mono text-foreground"
                                title={selected.failedLocator}>{selected.failedLocator}</code>
                            </div>
                          ) : (
                            <Input
                              value={overrideOriginalInput}
                              onChange={(e) => setOverrideOriginalInput(e.target.value)}
                              placeholder="Original locator this replaces (the one the runner uses today)"
                              aria-label="Original locator"
                              className={`h-8 font-mono text-xs ${FIELD} placeholder:text-muted-foreground/70`}
                            />
                          )}
                          <div className="flex min-w-0 gap-2 text-xs">
                            <span className="w-24 shrink-0 text-muted-foreground">Replacement:</span>
                            <code className="min-w-0 break-all font-mono text-success"
                              title={xpathResult.xpath}>{xpathResult.xpath}</code>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {selected?.igVersion ? (
                              <span className="text-xs text-muted-foreground">
                                Instagram <span className="font-mono text-foreground">{selected.igVersion}</span>
                              </span>
                            ) : (
                              <Input
                                value={overrideIgVersionInput}
                                onChange={(e) => setOverrideIgVersionInput(e.target.value)}
                                placeholder={overrideVersion
                                  ? `Instagram version (blank = ${overrideVersion})`
                                  : 'Instagram version, e.g. 424.0.0.49.64'}
                                aria-label="Instagram version for this override"
                                className={`h-8 w-64 text-xs ${FIELD} placeholder:text-muted-foreground/70`}
                              />
                            )}
                            <Input
                              value={overrideNotes}
                              onChange={(e) => setOverrideNotes(e.target.value)}
                              placeholder="Notes (optional)"
                              aria-label="Notes for this override"
                              className={`h-8 min-w-[10rem] flex-1 text-xs ${FIELD} placeholder:text-muted-foreground/70`}
                            />
                            <Button size="sm" onClick={saveOverride} disabled={savingOverride}>
                              {savingOverride ? <Loader2 size={14} className="animate-spin" />
                                : <><Save size={14} className="mr-1" /> Save override</>}
                            </Button>
                          </div>
                          {overrideSaveResult && (
                            <div className={`rounded-md border px-2 py-1.5 text-xs leading-snug ${overrideSaveResult.ok ? VERDICT_TONE.UNIQUE : VERDICT_TONE.ERROR}`}>
                              {overrideSaveResult.text}
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-[11px] leading-snug text-muted-foreground">
                        Evaluated on the server with the same XPath engine the runner uses.
                        AMBIGUOUS means the runner would take the first match and act on the wrong
                        element without noticing.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---- the permanent fixes: V2's xpath_overrides, kept server-side as locator_override ---- */}
      <div className="flex shrink-0 flex-col rounded-lg border border-border">
        <PanelHeader title="Locator overrides" icon={Wrench}
          collapsed={collapsedPanels.overrides} onToggle={() => togglePanel('overrides')}
          extra={
            <span className="text-xs">
              {overridesLoading ? 'loading…'
                : overrideVersion ? `${overrides.length} for ${overrideVersion}`
                : `${overrides.length} newest, all versions`}
            </span>
          } />
        {!collapsedPanels.overrides && (
          <div className="flex min-h-0 flex-col">
            <div className={`flex flex-wrap items-center gap-2 p-2 ${PANEL_BAR}`}>
              <Input
                value={overrideVersionInput}
                onChange={(e) => setOverrideVersionInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setOverrideVersion(overrideVersionInput.trim()); }}
                onBlur={() => setOverrideVersion(overrideVersionInput.trim())}
                placeholder="All versions - type an Instagram version to filter, then Enter"
                aria-label="Filter overrides by Instagram version"
                className={`h-8 w-96 max-w-full text-xs ${FIELD} placeholder:text-muted-foreground/70`}
              />
              {overrideVersion && (
                <Button size="sm" variant="ghost" className="h-8 px-2 text-xs"
                  onClick={() => { setOverrideVersionInput(''); setOverrideVersion(''); }}>
                  <X size={12} className="mr-1" /> All versions
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={loadOverrides} disabled={overridesLoading}>
                <RefreshCw size={14} className={`mr-1 ${overridesLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
            {overridesError && (
              <div className="flex items-start gap-2 border-b border-border bg-destructive/10 px-3 py-2 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{overridesError}</span>
              </div>
            )}
            <div className="max-h-56 overflow-auto">
              {overrides.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">
                  {overridesLoading ? 'Loading overrides…'
                    : overridesError ? 'Nothing could be loaded - see the message above.'
                    : overrideVersion ? `No overrides recorded for Instagram ${overrideVersion}.`
                    : 'No overrides recorded yet.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className={`${LABEL} pl-3`}>#</TableHead>
                      <TableHead className={LABEL}>Version</TableHead>
                      <TableHead className={LABEL}>Source</TableHead>
                      <TableHead className={LABEL}>Original</TableHead>
                      <TableHead className={LABEL}>Replacement</TableHead>
                      <TableHead className={`${LABEL} text-right`}>Confirmed</TableHead>
                      <TableHead className={LABEL}>Created</TableHead>
                      <TableHead className={LABEL}>State</TableHead>
                      <TableHead className="pr-3" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overrides.map((o) => (
                      <TableRow key={o.id}
                        className={`border-border hover:bg-secondary/60 ${o.live ? '' : 'text-muted-foreground opacity-60'}`}>
                        <TableCell className="pl-3 font-mono text-xs tnum">{o.id}</TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs">{o.igVersion}</TableCell>
                        <TableCell>
                          <Badge className={o.source === 'ADMIN'
                            ? 'bg-info/15 text-info border-transparent'
                            : 'bg-secondary text-muted-foreground border-transparent'}>
                            {o.source || '?'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[16rem] font-mono text-xs">
                          <span className="block truncate" title={o.original || ''}>{o.original}</span>
                        </TableCell>
                        <TableCell className="max-w-[16rem] font-mono text-xs">
                          <span className="block truncate" title={o.replacement || ''}>{o.replacement}</span>
                        </TableCell>
                        <TableCell className="text-right text-xs tnum">{o.confirmations ?? 0}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs" title={o.createdAt || ''}>
                          {o.createdBy || '?'}
                          <span className="text-muted-foreground"> · {whenOverride(o.createdAt)}</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {o.live ? (
                            <Badge className="bg-success/15 text-success border-transparent">live</Badge>
                          ) : (
                            <span title={`Retired ${whenOverride(o.retiredAt)} by ${o.retiredBy || '?'}: ${o.retiredReason || 'no reason recorded'}`}>
                              <Badge className="bg-muted text-muted-foreground border-transparent">retired</Badge>
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="pr-3 text-right">
                          {o.live && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => retireOverride(o)} disabled={retiringId === o.id}>
                              {retiringId === o.id ? <Loader2 size={12} className="animate-spin" />
                                : <><Ban size={12} className="mr-1" /> Retire</>}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <p className="border-t border-border px-3 py-2 text-[11px] leading-snug text-muted-foreground">
              A live override replaces its original locator on every runner from that runner's next
              session start; retiring it stops that just as fast. Hover an original or replacement
              for the full locator, and "retired" for who retired it and why.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function UIDebugViewerPage() {
  return (
    <DebugErrorBoundary>
      <UIDebugViewer />
    </DebugErrorBoundary>
  );
}
